import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Card } from "../ui/Card.tsx";
import { Button } from "../ui/Button.tsx";
import { Input } from "../ui/Input.tsx";
import { Label } from "../ui/Label.tsx";
import {
  Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight,
  Crown, Shield, Download, Settings, Database, Eye, EyeOff,
  Users, Check, ChevronsUpDown, X, CheckCircle2, Zap, UserPlus, Lock
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../../apiWithAccessToken.js";
import { createPortal } from "react-dom";
import SkeletonLoader from "../ui/SkeletonLoader";
import { sendTestEmail } from "../../apiWithSmtp";
import GroupsTab from "./GroupsTab.jsx";
import {getGroupDBOptions, getGroupTableOptions,createGroup}from "../../apiGroups";
import { resetAppStorageForLogout } from "../../utils/scopedStorage";

const defaultFormData = {
  name: "",
  email: "",
  password: "",
  role: "explorer",
  designation: "",
  status: "Active",
  isDownloadAllowed: "Yes",
  tokenLimit: "",
  lastLoginDate: ""
};


const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validatePassword = (password) => {
  if (password.length < 8) return "At least 8 characters";
  if (!/[A-Z]/.test(password)) return "One uppercase letter required";
  if (!/[a-z]/.test(password)) return "One lowercase letter required";
  if (!/\d/.test(password)) return "One number required";
  return null;
};

const getDefaultTokenLimitMode = ({ isDemoTenant, demoTokenLimit }) =>
  isDemoTenant && demoTokenLimit > 0 ? "max_demo" : "unlimited";

const getDefaultTokenLimitValue = ({ isDemoTenant, demoTokenLimit }) =>
  isDemoTenant && demoTokenLimit > 0 ? String(demoTokenLimit) : "";
const formatGroupDBSummary = (group) => {
  if (!group?.assigned_databases?.length) return "No DB access";

  const names = group.assigned_databases.map(d => d.title);

  if (names.length <= 2) return names.join(" • ");

  return `${names.slice(0, 2).join(" • ")} +${names.length - 2} more`;
};

const normalizeAssignedDatabases = (entries = []) =>
  entries
    .map((entry) => ({
      id: Number(entry?.id ?? entry?.source_db_config_id),
      allowed_tables: [...(entry?.allowed_tables || entry?.assigned_tables || [])].sort(),
    }))
    .filter((entry) => Number.isFinite(entry.id))
    .sort((a, b) => a.id - b.id);

const assignedDatabasesEqual = (left = [], right = []) =>
  JSON.stringify(normalizeAssignedDatabases(left)) === JSON.stringify(normalizeAssignedDatabases(right));

const clearAuthStorageAndRedirectToLogin = (message) => {
  if (message) {
    sessionStorage.setItem("post_login_notice", message);
  }
  resetAppStorageForLogout();
  window.location.href = "/login";
};
// ─────────────────────────────────────────────────────────────
// Searchable Group Combobox
// ─────────────────────────────────────────────────────────────
function GroupCombobox({ groupOptions, selectedGroupId, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  const selected = groupOptions.find(g => g.id === selectedGroupId);

  const filtered = groupOptions.filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="w-full h-10 px-3 flex items-center justify-between rounded-lg border border-border bg-background text-sm"
      >


        <div className="flex flex-col items-start text-left">
  <span className="truncate">
    {selected ? selected.name : "Search and select a group..."}
  </span>

  {selected && (
    <span className="text-[10px] text-muted-foreground">
      {formatGroupDBSummary(selected)}
    </span>
  )}
</div>
        <ChevronsUpDown size={14} />
      </button>

      {open && (
        <div className="  absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-40">
          <div className="p-2">
            <Input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search groups..."
              className="h-8 text-xs"
            />
          </div>

          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground text-center">
                No groups found
              </div>
            ) : (
              filtered.map(g => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => {
                    onSelect(g.id);
                    setOpen(false);
                    setSearch("");
                  }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-muted"
                >
                  <div className="flex flex-col">
      <span className="font-medium">{g.name}</span>
      <span className=" text-[10px] text-primary text-right">
        {formatGroupDBSummary(g)}
      </span>
    </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
// Inline Create Group Form
// ─────────────────────────────────────────────────────────────────────────────

function InlineCreateGroupForm({ onGroupCreated, onCancel }) {
  const [step, setStep]                 = useState(1);
  const [formData, setFormData]         = useState({ name: "", description: "" });
  const [errors, setErrors]             = useState({});
  const [dbOptions, setDbOptions]       = useState([]);
  const [selectedDBs, setSelectedDBs]   = useState([]);
  const [activeDBId, setActiveDBId]     = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const activeDB                        = selectedDBs.find((d) => d.id === activeDBId) || null;

  useEffect(() => {
    getGroupDBOptions().then((r) => setDbOptions(r.data || [])).catch(() => toast.error("Failed to load databases"));
  }, []);

  const validateStep1 = () => {
    const e = {};
    if (!formData.name.trim())               e.name = "Group name is required";
    else if (formData.name.trim().length < 2) e.name = "Name must be at least 2 characters";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleAddDB = async (db) => {
    if (selectedDBs.some((d) => d.id === db.id)) { setActiveDBId(db.id); return; }
    setTableLoading(true);
    try {
      const r = await getGroupTableOptions(db.id, null);
      setSelectedDBs((p) => [...p, { id: db.id, title: db.title, subtitle: db.subtitle || "", assigned_tables: r.data.assigned_tables || [], available_tables: r.data.available_tables || [] }]);
      setActiveDBId(db.id);
    } catch { toast.error("Failed to load tables"); }
    finally { setTableLoading(false); }
  };

  const handleRemoveDB = (id) => { setSelectedDBs((p) => p.filter((d) => d.id !== id)); if (activeDBId === id) setActiveDBId(null); };

  const toggle = (table) => setSelectedDBs((p) => p.map((db) => {
    if (db.id !== activeDBId) return db;
    const assigned = db.assigned_tables.includes(table);
    return { ...db, assigned_tables: assigned ? db.assigned_tables.filter((t) => t !== table) : [...db.assigned_tables, table], available_tables: assigned ? [...db.available_tables, table] : db.available_tables.filter((t) => t !== table) };
  }));

  const allTables = activeDB ? [...activeDB.assigned_tables, ...activeDB.available_tables] : [];
  const selectAll = () => setSelectedDBs((p) => p.map((db) => db.id !== activeDBId ? db : { ...db, assigned_tables: allTables, available_tables: [] }));
  const clearAll  = () => setSelectedDBs((p) => p.map((db) => db.id !== activeDBId ? db : { ...db, assigned_tables: [], available_tables: allTables }));

  const handleSubmit = async () => {
    if (!selectedDBs.length) { toast.error("Assign at least one database"); return; }
    const bad = selectedDBs.find((d) => !d.assigned_tables.length);
    if (bad) { toast.error(`Select at least one table for "${bad.title}"`); return; }
    setIsSubmitting(true);
    try {
      const payload = { name: formData.name.trim(), description: formData.description.trim(), assigned_databases: selectedDBs.map((d) => ({ id: d.id, allowed_tables: d.assigned_tables })) };
      const res     = await createGroup(payload);
      onGroupCreated({ id: res.data.id, name: payload.name, description: payload.description, assigned_databases: payload.assigned_databases });
    } catch (err) { toast.error(err.response?.data?.message || "Failed to create group"); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="rounded-2xl border-2 border-border bg-surface overflow-hidden">
      {/* Mini header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-muted/30 border-b border-border">
        <div className="flex items-center gap-2.5">
          {[{ label: "Details" }, { label: "Databases" }].map((s, i) => {
            const n = i + 1;
            return (
              <div key={n} className="flex items-center gap-1.5">
                <div className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center transition-all
                  ${step === n ? "bg-primary text-primary-foreground" : step > n ? "bg-primary/80 text-primary-foreground" : "bg-background border border-border text-muted-foreground"}`}>
                  {step > n ? "✓" : n}
                </div>
                <span className={`text-[11px] font-semibold ${step === n ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
                {i < 1 && <ChevronRight size={11} className="text-muted-foreground mx-0.5" />}
              </div>
            );
          })}
        </div>
        <button type="button" onClick={onCancel} className="p-1 rounded-lg hover:bg-muted text-muted-foreground transition-colors"><X size={13} /></button>
      </div>

      <div className="p-4">
        {step === 1 && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Group Name *</Label>
              <Input value={formData.name} onChange={(e) => { setFormData((p) => ({ ...p, name: e.target.value })); setErrors((p) => ({ ...p, name: null })); }} placeholder="e.g. Finance Team" className={`h-9 bg-background border-border focus-visible:ring-primary ${errors.name ? "border-destructive" : ""}`} autoFocus />
              {errors.name && <p className="text-[10px] text-destructive font-medium">{errors.name}</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Description <span className="font-normal opacity-50">(optional)</span></Label>
              <Input value={formData.description} onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))} placeholder="What is this group for?" className="h-9 bg-background border-border focus-visible:ring-primary" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="flex gap-3" style={{ minHeight: 210 }}>
            {/* DB column */}
            <div className="w-32 shrink-0 space-y-1">
              <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Databases</p>
              {dbOptions.length === 0 && <p className="text-[10px] text-muted-foreground italic">None available</p>}
              {dbOptions.map((db) => {
                const sel    = selectedDBs.some((d) => d.id === db.id);
                const active = activeDBId === db.id;
                const count  = selectedDBs.find((d) => d.id === db.id)?.assigned_tables?.length || 0;
                return (
                  <button key={db.id} type="button" onClick={() => handleAddDB(db)}
                    className={`w-full text-left px-2.5 py-2 rounded-xl text-xs transition-all border-2
                      ${active ? "bg-primary text-primary-foreground border-primary shadow-md" : sel ? "bg-primary/10 text-foreground border-primary/30" : "bg-background border-border hover:border-primary/30 text-foreground"}`}>
                    <div className="flex items-center gap-1.5">
                      <Database className="w-3 h-3 shrink-0 opacity-70" />
                      <span className="font-semibold truncate">{db.title}</span>
                    </div>
                    {sel && <div className={`text-[9px] mt-1 font-bold ${active ? "opacity-80" : "text-primary"}`}>{count} table{count !== 1 ? "s" : ""}</div>}
                  </button>
                );
              })}
            </div>

            {/* Table column */}
            <div className="flex-1 min-w-0 border-l border-border pl-3 flex flex-col">
              {!activeDB ? (
                <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                  <Database className="w-8 h-8 opacity-10 mb-2" />
                  <p className="text-[11px] text-center">Select a database</p>
                </div>
              ) : tableLoading ? (
                <div className="space-y-1.5">{[...Array(4)].map((_, i) => <SkeletonLoader key={i} className="h-7 rounded-lg" />)}</div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-border">
                    <div>
                      <p className="text-xs font-semibold text-foreground">{activeDB.title}</p>
                      <p className="text-[10px] text-muted-foreground">{activeDB.assigned_tables.length}/{allTables.length} selected</p>
                    </div>
                    <div className="flex gap-2 text-[10px]">
                      <button type="button" onClick={selectAll} className="text-primary hover:underline font-semibold">All</button>
                      <span className="text-muted-foreground">·</span>
                      <button type="button" onClick={clearAll} className="text-muted-foreground hover:underline">None</button>
                      <span className="text-muted-foreground">·</span>
                      <button type="button" onClick={() => handleRemoveDB(activeDB.id)} className="text-destructive hover:underline">Remove</button>
                    </div>
                  </div>
                  <div className="flex-1 max-h-40 overflow-y-auto no-scrollbar space-y-0.5">
                    {allTables.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">No tables</p> : allTables.map((t) => {
                      const checked = activeDB.assigned_tables.includes(t);
                      return (
                        <label key={t} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-muted cursor-pointer group">
                          <input type="checkbox" checked={checked} onChange={() => toggle(t)} className="rounded border-border text-primary focus:ring-primary h-3.5 w-3.5" />
                          <span className={`text-xs flex-1 transition-colors ${checked ? "text-foreground font-semibold" : "text-muted-foreground group-hover:text-foreground"}`}>{t}</span>
                          {checked && <CheckCircle2 className="w-3 h-3 text-primary shrink-0" />}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/20">
        {step === 1 ? (
          <>
            <button type="button" onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground font-medium transition-colors">Cancel</button>
            <Button type="button" onClick={() => { if (validateStep1()) setStep(2); }} className="h-8 px-4 text-xs bg-primary hover:bg-primary/90 text-primary-foreground border-0">
              Next <ChevronRight size={12} className="ml-1" />
            </Button>
          </>
        ) : (
          <>
            <Button type="button" variant="outline" onClick={() => setStep(1)} className="h-8 text-xs border-border text-foreground hover:bg-muted">
              <ChevronLeft size={12} className="mr-1" /> Back
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={isSubmitting || !selectedDBs.length} className="h-8 px-4 text-xs bg-primary hover:bg-primary/90 text-primary-foreground border-0 disabled:opacity-50">
              {isSubmitting ? "Creating..." : "Create Group"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [step, setStep] = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ ...defaultFormData });
  const isSuperAdmin = editingUser?.role === "super_admin";
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [tokenLimitMode, setTokenLimitMode] = useState("unlimited");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [activeAccessTab, setActiveAccessTab] = useState("users");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 4;
  const [dbOptions, setDbOptions] = useState([]);
  const [selectedDBs, setSelectedDBs] = useState([]);
const [activeDBId, setActiveDBId] = useState(null);
const [tableLoading, setTableLoading] = useState(false);
  //  NEW — access mode system
const [accessMode, setAccessMode] = useState("direct");
const [groupOptions, setGroupOptions] = useState([]);
const [selectedGroupId, setSelectedGroupId] = useState(null);
const [inheritedAccess, setInheritedAccess] = useState([]);
  const [runtimeInfo, setRuntimeInfo] = useState(null);
  const [quotaPolicy, setQuotaPolicy] = useState(null);

  const demoTokenLimit = Number(quotaPolicy?.tenant_demo_token_limit ?? 0);
  const isDemoTenant =
    runtimeInfo?.billing_mode === "demo" &&
    Boolean(quotaPolicy?.tenant_demo_cap_applies) &&
    demoTokenLimit > 0;

  const validateStep1 = () => {
  const newErrors = {};
  const emailToValidate = formData.email.trim().toLowerCase();

    // 1. Name Validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    // 2. Designation Validation
    if (!formData.designation?.trim()) {
      newErrors.designation = "Designation is required";
    }

    // 2. Email Validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Invalid email format";
    } else {
      // --- NEW CODE: DUPLICATE CHECK ---
      // Check if email exists in the locally fetched 'users' list
      const isDuplicate = users.some(existingUser => {
        // If editing, ignore the user's own email (allow them to keep their current email)
        if (editingUser && existingUser.id === editingUser.id) {
          return false;
        }
        return existingUser.email.toLowerCase() === emailToValidate;
      });

      if (isDuplicate) {
        newErrors.email = "Email already registered";
      }
    }

    // 3. Password Validation
    if (!editingUser) {
      if (!formData.password.trim()) {
        newErrors.password = "Password is required";
      } else {
        const pwdError = validatePassword(formData.password);
        if (pwdError) newErrors.password = pwdError;
      }
    } else if (showPasswordChange) {
      if (!formData.password.trim()) {
        newErrors.password = "New password is required";
      } else {
        const pwdError = validatePassword(formData.password);
        if (pwdError) newErrors.password = pwdError;
      }
    }

    // 4. Token limit Validation
    // 4. Token limit Validation
    // Only validate if we are in "Custom" mode
    if (tokenLimitMode === "custom") {
      if (!formData.tokenLimit || formData.tokenLimit === "") {
        newErrors.tokenLimit = "Please enter a limit value";
      } else {
        const token = Number(formData.tokenLimit);
        if (Number.isNaN(token) || token < 1) {
          newErrors.tokenLimit = "Token limit must be a positive number";
        } else if (isDemoTenant && token > demoTokenLimit) {
          newErrors.tokenLimit = `Token limit cannot exceed the demo cap of ${demoTokenLimit}`;
        }
      }
    }

    setErrors(newErrors);
    
    // If we have errors, the function returns false and Step 2 is blocked
    return Object.keys(newErrors).length === 0;
  };



  const formatRole = (role) => {
  if (!role) return "Explorer";
  return role
    .toLowerCase()
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const fetchDBOptions = useCallback(async () => {
  try {
    const res = await api.get("/users/db-options");
    setDbOptions(res.data || []);
  } catch (err) {
    toast.error("Failed to load database options");
  }
}, []);
const activeDB = selectedDBs.find(d => d.id === activeDBId) || null;

const handleAddDB = async (dbId) => {
  if (!dbId) return;
  if (selectedDBs.some(d => d.id === dbId)) { setActiveDBId(dbId); return; }
  setTableLoading(true);
  try {
    const url = editingUser
      ? `/users/table-options?config_id=${dbId}&user_id=${editingUser.id}`
      : `/users/table-options?config_id=${dbId}`;
    const res = await api.get(url);
    const dbLabel = dbOptions.find(d => d.id === dbId)?.display_label || String(dbId);
    setSelectedDBs(p => [...p, {
      id: dbId,
      title: dbLabel,
      assigned_tables: res.data.assigned_tables || [],
      available_tables: res.data.available_tables || [],
    }]);
    setActiveDBId(dbId);
  } catch { toast.error("Failed to load tables"); }
  finally { setTableLoading(false); }
};

const handleRemoveDB = (id) => {
  setSelectedDBs(p => p.filter(d => d.id !== id));
  if (activeDBId === id) setActiveDBId(null);
};

const toggleTable = (table) => setSelectedDBs(p => p.map(db => {
  if (db.id !== activeDBId) return db;
  const assigned = db.assigned_tables.includes(table);
  return {
    ...db,
    assigned_tables: assigned ? db.assigned_tables.filter(t => t !== table) : [...db.assigned_tables, table],
    available_tables: assigned ? [...db.available_tables, table] : db.available_tables.filter(t => t !== table),
  };
}));



 const fetchUsers = useCallback(async () => {
  setIsLoading(true);
  try {
    const resp = await api.get("/users/");
    const rawUsers = resp.data?.users || resp.data;

    const mappedUsers = rawUsers.map(user => ({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role?.toLowerCase(),
      designation: user.designation || "",
      group_id: user.group_id,
      assigned_databases: user.assigned_databases || [],
      status: user.status === "1" ? "Active" : "Inactive",
      isDownloadAllowed: user.is_download_allowed === "1" ? "Yes" : "No",
      tokenLimit:
        user.token_limit === null || user.token_limit === -1
          ? ""
          : String(user.token_limit),
      lastLoginDate: user.last_login_date || null,
      source_db_config_id: user.source_db_config_id

    }) );

    setUsers(mappedUsers);
  } catch (err) {
    console.error(err);
    toast.error("Failed to load users");
  } finally {
    setIsLoading(false);
  }
}, []);

const handleAccessModeChange = (mode) => {
  setAccessMode(mode);
  setSelectedGroupId(null);
  setSelectedDBs([]);
  setActiveDBId(null);
  setInheritedAccess([]);
};

const resolveTokenLimitForPayload = useCallback(() => {
  if (isDemoTenant) {
    if (tokenLimitMode === "max_demo") {
      return demoTokenLimit;
    }
    return Number(formData.tokenLimit);
  }

  if (tokenLimitMode === "unlimited") {
    return -1;
  }

  return Number(formData.tokenLimit);
}, [demoTokenLimit, formData.tokenLimit, isDemoTenant, tokenLimitMode]);

const fetchCurrentTenantPolicy = useCallback(async () => {
  try {
    const { data } = await api.get("/auth/me");
    setRuntimeInfo(data?.runtime || null);
    setQuotaPolicy(data?.quota_policy || null);
  } catch {
    // Keep user management usable even if policy metadata fails to load.
  }
}, []);

const fetchGroupsList = useCallback(async () => {
  try {
    const r = await api.get("/groups/");
    setGroupOptions(r.data || []);
  } catch {
    toast.error("Failed to load groups");
  }
}, []);

useEffect(() => {
fetchUsers();
fetchDBOptions(); 
fetchGroupsList();
fetchCurrentTenantPolicy();
}, [fetchUsers, fetchDBOptions,fetchGroupsList, fetchCurrentTenantPolicy]);

useEffect(() => {
  if (!showForm || editingUser || !isDemoTenant) {
    return;
  }

  if (tokenLimitMode === "unlimited" || formData.tokenLimit === "") {
    setTokenLimitMode("max_demo");
    setFormData((prev) => ({
      ...prev,
      tokenLimit: String(demoTokenLimit),
    }));
  }
}, [demoTokenLimit, editingUser, formData.tokenLimit, isDemoTenant, showForm, tokenLimitMode]);

const handleNewGroupCreated = (ng) => {
  setGroupOptions((p) => [...p, ng]);
  setSelectedGroupId(ng.id);
  setAccessMode("existing");
  toast.success(`Group "${ng.name}" created!`);
};

  const getCurrentAccessPayload = () => {
    if (accessMode === "existing" && selectedGroupId) {
      return {
        group_id: Number(selectedGroupId),
        assigned_databases: [],
      };
    }

    return {
      group_id: null,
      assigned_databases: selectedDBs.map((d) => ({
        id: Number(d.id),
        allowed_tables: [...(d.assigned_tables || [])],
      })),
    };
  };

  const buildCreatePayload = () => {
    const accessPayload = getCurrentAccessPayload();
    const payload = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      role: formData.role,
      designation: formData.designation,
      token_limit: resolveTokenLimitForPayload(),
      status: formData.status === "Active" ? "1" : "0",
      is_download_allowed: formData.isDownloadAllowed === "Yes" ? "1" : "0",
      ...accessPayload,
    };

    if (editingUser?.role === "super_admin") {
      payload.role = "super_admin";
      payload.status = "1";
      payload.is_download_allowed = "1";
      payload.token_limit = -1;
    }

    return payload;
  };

  const buildUpdatePayload = () => {
    const payload = {};
    const nextAccess = getCurrentAccessPayload();
    const previousAccess = {
      group_id: editingUser?.group_id ?? null,
      assigned_databases: editingUser?.group_id ? [] : (editingUser?.assigned_databases || []),
    };

    if (formData.name !== editingUser.name) payload.name = formData.name;
    if (formData.email !== editingUser.email) payload.email = formData.email;
    if (formData.designation !== (editingUser.designation || "")) payload.designation = formData.designation;

    const nextRole = formData.role;
    if (nextRole !== editingUser.role) payload.role = nextRole;

    const nextStatus = formData.status === "Active" ? "1" : "0";
    const previousStatus = editingUser.status === "Active" ? "1" : "0";
    if (nextStatus !== previousStatus) payload.status = nextStatus;

    const nextDownloadAllowed = formData.isDownloadAllowed === "Yes" ? "1" : "0";
    const previousDownloadAllowed = editingUser.isDownloadAllowed === "Yes" ? "1" : "0";
    if (nextDownloadAllowed !== previousDownloadAllowed) payload.is_download_allowed = nextDownloadAllowed;

    const nextTokenLimit = resolveTokenLimitForPayload();
    const previousTokenLimit =
      editingUser.tokenLimit === "" || editingUser.tokenLimit === null
        ? -1
        : Number(editingUser.tokenLimit);
    if (nextTokenLimit !== previousTokenLimit) payload.token_limit = nextTokenLimit;

    if (showPasswordChange && formData.password.trim()) {
      payload.password = formData.password.trim();
    }

    if (
      Number(nextAccess.group_id) !== Number(previousAccess.group_id ?? null) ||
      !assignedDatabasesEqual(nextAccess.assigned_databases, previousAccess.assigned_databases)
    ) {
      payload.group_id = nextAccess.group_id;
      payload.assigned_databases = nextAccess.assigned_databases;
    }

    return payload;
  };



  const handleSubmit = async (e) => {
    e.preventDefault(); // Stop default browser reload

    // === FIX 1: Handle "Enter" key on Step 1 ===
    // If user hits Enter on Step 1, validate and move to Step 2 instead of submitting
    if (step === 1) {
      if (validateStep1()) {
        setStep(2);
      }
      return; // Stop execution here
    }

    // === STEP 2: Actual Submission ===
    setIsSubmitting(true);

   // Access validation
if (accessMode === "existing" && !selectedGroupId) {
  toast.error("Please select a group");
  setIsSubmitting(false);
  return;
}

if (accessMode === "direct" && !selectedDBs.length === 0) {
  toast.error("Select a database to continue");
  setIsSubmitting(false);
  return;
}

    try {
      if (editingUser) {
        const payload = buildUpdatePayload();
        if (Object.keys(payload).length === 0) {
          toast.info("No changes to save");
          handleCancel();
          return;
        }
        const response = await api.put(`/users/${editingUser.id}`, payload);
        toast.success("User updated successfully");
        if (response.data?.session_action === "relogin_required") {
          toast.info("Your login details changed. Please sign in again.");
          handleCancel();
          setTimeout(() => {
            clearAuthStorageAndRedirectToLogin("Your login details changed. Please sign in again.");
          }, 300);
          return;
        }
      }
       else {
        const payload = buildCreatePayload();
        await api.post("/users/", payload);
          toast.success("User created successfully");
          try {
    const url = process.env.REACT_APP_WELCOME_URL;

    await sendTestEmail({
      recipient_email: payload.email,
      password: payload.password,
      url: url
    });

    toast.success("Welcome email sent to user");
  } catch (err) {
    console.error("Welcome email failed:", err);
    toast.warn("User created, but welcome email failed");
  }
  
         
   }
      
      await fetchUsers();
      handleCancel();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.msg || err.response?.data?.message || err.response?.data || err.message || "Operation failed";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = user => {
    setEditingUser(user);
    setStep(1);
    setShowPassword(false);
    setShowPasswordChange(false);
    const isSuperAdmin = user.role === "super_admin";

    // Detect if this user has a limit or is unlimited
    const hasLimit = user.tokenLimit && user.tokenLimit !== "" && user.tokenLimit !== "-1";
    if (isDemoTenant) {
      setTokenLimitMode(
        hasLimit && Number(user.tokenLimit) < demoTokenLimit ? "custom" : "max_demo"
      );
    } else {
      setTokenLimitMode(hasLimit ? "custom" : "unlimited");
    }

    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: isSuperAdmin ? "super_admin" : user.role, 
      designation: user.designation || "",
      status: user.status,
      isDownloadAllowed: user.isDownloadAllowed,
      // If unlimited, keep it empty string. If limited, show the number.
      tokenLimit: isDemoTenant
        ? hasLimit
          ? user.tokenLimit
          : String(demoTokenLimit)
        : hasLimit
          ? user.tokenLimit
          : "",
      lastLoginDate: user.lastLoginDate
    });
// detect group-based access (if your API provides group_id)
if (user.group_id) {
  setAccessMode("existing");
  setSelectedGroupId(user.group_id);
} else {
  setAccessMode("direct");
  // Load direct access DBs from the detail endpoint
  api.get(`/users/${user.id}`).then(res => {
    const directAccess = res.data.direct_access || [];
    if (directAccess.length > 0) {
      Promise.all(
        directAccess.map(async (acc) => {
          const r = await api.get(`/users/table-options?config_id=${acc.id}&user_id=${user.id}`);
          return {
            id: acc.id,
            title: acc.title,
            assigned_tables: r.data.assigned_tables || [],
            available_tables: r.data.available_tables || [],
          };
        })
      ).then(loaded => {
        setSelectedDBs(loaded);
        setActiveDBId(loaded[0]?.id || null);
      });
    }
  }).catch(() => toast.error("Failed to load user DB access"));
}
    setShowForm(true);
  };

  const handleDelete = async id => {
  if (!window.confirm("Are you sure you want to delete this user?")) return;

  try {
    await api.delete(`/users/${id}`);
    toast.success("User deleted successfully");

    setUsers(prev => {
      const updated = prev.filter(u => u.id !== id);
      if ((currentPage - 1) * rowsPerPage >= updated.length) {
        setCurrentPage(p => Math.max(1, p - 1));
      }
      return updated;
    });
  } catch (err) {
    console.error(err);
    toast.error("Failed to delete user");
  }
};


  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setStep(1);
    setShowPassword(false);
    setShowPasswordChange(false);
     //  reset access system
    setAccessMode("direct");
    setSelectedGroupId(null);
    setInheritedAccess([]);
    //reset direct access
    setSelectedDBs([]);
    setActiveDBId(null);

    setFormData({
      ...defaultFormData,
      tokenLimit: getDefaultTokenLimitValue({ isDemoTenant, demoTokenLimit }),
    });
    setTokenLimitMode(getDefaultTokenLimitMode({ isDemoTenant, demoTokenLimit }));
    setErrors({});
  };

  const handleOpenCreateUser = () => {
    setEditingUser(null);
    setStep(1);
    setShowPassword(false);
    setShowPasswordChange(false);
    setAccessMode("direct");
    setSelectedGroupId(null);
    setInheritedAccess([]);
    setSelectedDBs([]);
    setActiveDBId(null);
    setErrors({});
    setTokenLimitMode(getDefaultTokenLimitMode({ isDemoTenant, demoTokenLimit }));
    setFormData({
      ...defaultFormData,
      tokenLimit: getDefaultTokenLimitValue({ isDemoTenant, demoTokenLimit }),
    });
    setShowForm(true);
  };
 const filteredUsers = useMemo(() => {
  return users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );
}, [users, searchTerm]);

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  useEffect(() => {
  setCurrentPage(1);
}, [searchTerm]);


  useEffect(() => {
    if (currentPage > totalPages) 
 setCurrentPage(p => Math.min(p, totalPages || 1))
  }, [filteredUsers, totalPages]);


return (
  <div className="w-full flex flex-col gap-2">

    {/* ROW 1: Title + Tab Switcher side by side */}
    <div className="flex items-center justify-between">
      <h1 className="text-base font-semibold text-foreground flex items-center gap-2">
        <Settings className="w-4 h-4 text-primary" />
        User Management
      </h1>

      <div className="inline-flex bg-muted/40 p-1 rounded-lg border border-border">
          {["users", "groups"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveAccessTab(tab)}
              className={`px-4 py-1.5 text-sm rounded-md transition-all font-medium capitalize
                ${activeAccessTab === tab
                  ? "bg-background shadow text-primary"
                  : "text-muted-foreground hover:text-foreground"}`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>


    {/* ── ROW 2: Count  +  Search  +  Add  (Users tab only) ── */}
      {activeAccessTab === "users" && (
        <div className="flex items-center justify-between ">
          

          <div className="flex gap-2">
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 h-9"
              />
            </div>
            <Button
              onClick={handleOpenCreateUser}
              disabled={showForm}
              className="h-9 px-4 flex items-center gap-2 whitespace-nowrap"
            >
              <Plus size={14} />
              Add User
            </Button>
          </div>
        </div>
      )}
     
     {activeAccessTab === "users" ? (
<>
      {/* DESKTOP TABLE  */}
      <div className="hidden md:block bg-background" >


    <table className="w-full text-sm">

<thead className="sticky top-0 z-30 bg-background shadow-sm">
        <tr className="text-left text-muted-foreground font-medium">

          <th className="px-5 py-2 w-[32%]">User</th>
          <th className="px-5 py-2 w-[20%]">Role & Status</th>

          <th className="px-5 py-2 w-[15%] hidden lg:table-cell">
            Access
          </th>
          <th className="px-5 py-2 w-[15%] hidden lg:table-cell">
           Group
          </th>
          <th className="px-5 py-2 w-[15%] hidden lg:table-cell">
           Databases
          </th>
          
          <th className="px-5 py-2 w-[16%] hidden xl:table-cell">
            Last Login
          </th>

          <th className="px-5 py-2 w-[12%] text-right">
            Actions
          </th>

        </tr>
      </thead>

      {/* ===== BODY ===== */}
      <tbody className="divide-y divide-border">

        {isLoading ? (
          Array.from({ length: rowsPerPage }).map((_, i) => (
            <tr key={i}>
              <td className="px-5 py-2">
                <SkeletonLoader className="h-4 w-40 mb-2" />
                <SkeletonLoader className="h-3 w-28" />
              </td>
              <td className="px-5 py-2">
                <SkeletonLoader className="h-4 w-24" />
              </td>
             <td className="px-5 py-2 hidden lg:table-cell">
  <SkeletonLoader className="h-4 w-28" />
</td>
<td className="px-5 py-2 hidden lg:table-cell">
  <SkeletonLoader className="h-4 w-24" />
</td>
<td className="px-5 py-2 hidden lg:table-cell">
  <SkeletonLoader className="h-4 w-28" />
</td>
<td className="px-5 py-2 hidden xl:table-cell">
  <SkeletonLoader className="h-4 w-20" />
</td>
<td className="px-5 py-2 text-right">
  <SkeletonLoader className="h-6 w-12 ml-auto" />
</td>
            </tr>
          ))
        ) : (
          paginatedUsers.map(user => (
            <tr
              key={user.id}
              className="hover:bg-muted/40 transition-colors"
            >

              {/* USER */}
              <td className="px-5 py-2">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white flex items-center justify-center font-semibold">
                    {user.name.charAt(0).toUpperCase()}
                  </div>

                  <div className="min-w-0">
                    <div className="font-medium text-foreground truncate">
                      {user.name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {user.email}
                    </div>
                  </div>
                </div>
              </td>

              {/* ROLE & STATUS */}
              <td className="px-5 py-2">
                <div className="flex flex-col gap-1">

                  <div className="flex items-center gap-1">
                    {user.role === "super_admin" ? (
                      <Crown className="w-4 h-4 text-purple-600" />
                    ) : user.role === "admin" ? (
                      <Crown className="w-4 h-4 text-yellow-600" />
                    ) : (
                      <Shield className="w-4 h-4 text-primary" />
                    )}

                    <span className="text-xs font-medium">
                      {formatRole(user.role)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-xs">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        user.status === "Active"
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    />
                    {user.status}
                  </div>

                </div>
              </td>

              {/* ACCESS */}
              <td className="px-5 py-2 hidden lg:table-cell">
                <div className="flex flex-col gap-1 text-xs">

                  <span
                    className={`inline-flex items-center gap-1 ${
                      user.isDownloadAllowed === "Yes"
                        ? "text-green-700"
                        : "text-muted-foreground"
                    }`}
                  >
                    <Download className="w-3 h-3" />
                    {user.isDownloadAllowed}
                  </span>

                  <span className="text-muted-foreground">
                   Tokens: {user.tokenLimit !== "" ? user.tokenLimit : "∞"}
                  </span>

                </div>
              </td>

              {/* DATABASES */}
{/* GROUP */}
<td className="px-5 py-2 hidden lg:table-cell">
  {user.group_id ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-700 bg-background border border-border px-1.5 py-0.5 rounded-full w-fit">
      <Users className="w-2.5 h-2.5" />
      {groupOptions.find(g => g.id === user.group_id)?.name || "Group"}
    </span>
  ) : (
    <span className="text-[10px] text-muted-foreground">Not Assigned</span>
  )}
</td>

{/* DATABASES */}
<td className="px-5 py-2 hidden lg:table-cell">
  {user.assigned_databases?.length > 0 ? (
    <div className="flex flex-wrap gap-1">
      {user.assigned_databases.slice(0, 2).map(db => (
        <span
          key={db.source_db_config_id}
          className="inline-flex items-center gap-1 text-[10px] bg-background text-blue-700 border border-border px-1.5 py-0.5 rounded-full"
        >
          <Database className="w-2.5 h-2.5" />
          {db.db_name}
        </span>
      ))}
      {user.assigned_databases.length > 2 && (
        <span className="text-[10px] text-muted-foreground px-1 py-0.5">
          +{user.assigned_databases.length - 2} more
        </span>
      )}
    </div>
  ) : (
    <span className="text-[10px] text-muted-foreground">—</span>
  )}
</td>
              {/* LAST LOGIN */}
              <td className="px-5 py-2 hidden xl:table-cell text-xs text-muted-foreground">
                {user.lastLoginDate
                  ? new Date(user.lastLoginDate).toLocaleDateString()
                  : "Never"}
              </td>

              {/* ACTIONS */}
              <td className="px-5 py-2 text-right">
                <div className="inline-flex items-center gap-2">

                  <Button
                    size="icon" className="h-9 w-9"
                    variant="ghost"
                    onClick={() => handleEdit(user)}
                  >
                    <Pencil size={16} />
                  </Button>

                  <Button
                    size="icon" className="h-9 w-9"
                    variant="ghost"
                    //className="text-red-500"
                    onClick={() => handleDelete(user.id)}
                  >
                    <Trash2 size={16} />
                  </Button>

                </div>
              </td>

            </tr>
          ))
        )}

      </tbody>
    </table>
  </div>
  

<div className="md:hidden space-y-3 flex-1 overflow-y-auto">
  {paginatedUsers.map(user => (
    <Card key={user.id} className="p-4 border border-border">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white flex items-center justify-center font-semibold">
          {user.name.charAt(0).toUpperCase()}
        </div>

        <div className="flex-1">
          <div className="font-medium">{user.name}</div>
          <div className="text-xs text-primary">{user.email}</div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-1 rounded bg-primary/10 text-primary">
          {formatRole(user.role)}
        </span>
        <span className={`px-2 py-1 rounded ${user.status === "Active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
          {user.status}
        </span>
        <span className="px-2 py-1 rounded bg-purple-100 text-purple-700">
          Tokens: {user.tokenLimit || "∞"}
        </span>
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <Button size="icon" variant="ghost" onClick={() => handleEdit(user)}>
          <Pencil size={14} />
        </Button>
        <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(user.id)}>
          <Trash2 size={14} />
        </Button>
      </div>
    </Card>
  ))}
</div>


<div className="flex items-center justify-between mt-4 shrink-0 px-1">

  {/* Left: User count */}
  <p className="text-xs text-muted-foreground">
    {!isLoading && (
      searchTerm
        ? <><span className="font-medium text-foreground">{filteredUsers.length}</span> of {users.length} users</>
        : <><span className="font-medium text-foreground">{users.length}</span> total users</>
    )}
  </p>

  {/* Right: Pagination */}
  {totalPages > 1 && (
    <div className="flex items-center gap-3 bg-muted/40 px-3 py-1.5 rounded-lg">

      <button
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
        className={`flex items-center justify-center w-7 h-7 rounded-md transition-all
          ${currentPage === 1
            ? "text-muted-foreground opacity-40 cursor-not-allowed"
            : "hover:bg-primary/10 hover:text-primary"
          }`}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <span className="text-xs font-medium text-muted-foreground tracking-wide">
        {currentPage} / {totalPages}
      </span>

      <button
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
        className={`flex items-center justify-center w-7 h-7 rounded-md transition-all
          ${currentPage === totalPages
            ? "text-muted-foreground opacity-40 cursor-not-allowed"
            : "hover:bg-primary/10 hover:text-primary"
          }`}
      >
        <ChevronRight className="h-4 w-4" />
      </button>

    </div>
  )}

</div>


</> ):(
<GroupsTab onGroupsChange={fetchGroupsList} />
    
)}


        {showForm &&
        createPortal(
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200 backdrop-blur-sm">
            
            {/* FIX 1: Changed 'h-auto' to 'h-[600px]'. 
                This forces the modal to stay one size. The footer will now stick to the bottom perfectly. 
                On small screens, 'h-[90vh]' takes priority. */}
            <div className="bg-background rounded-xl shadow-2xl w-full max-w-2xl h-[600px] max-h-[90vh] flex flex-col border border-border">
              
              {/* === HEADER === */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-border shrink-0 bg-muted/10">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight">
                    {editingUser ? "Edit User Details" : "Create New User"}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {step === 1 ? "General Information" : "Data Permissions"}
                  </p>
                </div>
                <button
                  onClick={handleCancel}
                  className="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-all"
                >
                  ✕
                </button>
              </div>

              {/* === FORM BODY === */}
              <form
                onSubmit={handleSubmit}
                className="flex flex-col flex-1 min-h-0"
              >
                {/* Scrollable Area (Only scrolls if screen is tiny) */}
                <div className="no-scrollbar px-6 py-6 flex-1 overflow-y-auto">
                  
                  {/* ====== STEP 1: USER DETAILS (COMPACT GRID) ====== */}
                  {step === 1 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
                      
                      {/* 1. Name */}
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-muted-foreground">Full Name</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => {
                            setFormData({ ...formData, name: e.target.value });
                            setErrors((prev) => ({ ...prev, name: null }));
                          }}
                          placeholder="Enter Name"
                          className={`h-9 ${errors.name ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                        />
                        {errors.name && <p className="text-[10px] text-red-500 font-medium">{errors.name}</p>}
                      </div>

                      {/* 2. Email */}
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-muted-foreground">Email Address</Label>
                        <Input
                          type="email"
                          name={editingUser ? "user-edit-email" : "user-email"}
                          autoComplete={editingUser ? "off" : "email"}
                          value={formData.email}
                          onChange={(e) => {
                            setFormData({ ...formData, email: e.target.value });
                            setErrors((prev) => ({ ...prev, email: null }));
                          }}
                          placeholder="Enter your email"
                          className={`h-9 ${errors.email ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                        />
                        {errors.email && <p className="text-[10px] text-red-500 font-medium">{errors.email}</p>}
                      </div>

                      {/* 3. Password */}
                      <div className="space-y-1.5">
                        <div className="flex justify-between">
                          <Label className="text-xs font-medium text-muted-foreground">
                            Password {editingUser && <span className="text-[10px] font-normal opacity-70">(Only when changing it)</span>}
                          </Label>
                          {editingUser && (
                            <button
                              type="button"
                              className="text-[10px] font-medium text-primary hover:underline"
                              onClick={() => {
                                setShowPasswordChange((current) => {
                                  const next = !current;
                                  if (!next) {
                                    setFormData((prev) => ({ ...prev, password: "" }));
                                    setShowPassword(false);
                                    setErrors((prev) => ({ ...prev, password: null }));
                                  }
                                  return next;
                                });
                              }}
                            >
                              {showPasswordChange ? "Cancel password change" : "Change password"}
                            </button>
                          )}
                        </div>
                        {(!editingUser || showPasswordChange) && (
                          <div className="relative">
                            <Input
                              type={showPassword ? "text" : "password"}
                              name={editingUser ? "user-edit-password" : "user-password"}
                              autoComplete="new-password"
                              value={formData.password}
                              onChange={(e) => {
                                setFormData({ ...formData, password: e.target.value });
                                setErrors((prev) => ({ ...prev, password: null }));
                              }}
                              className={`h-9 pr-9 ${errors.password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                              placeholder={editingUser ? "Enter a new password" : "Create password"}
                            />
                            <button
                              type="button"
                              className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                          </div>
                        )}
                        {editingUser && !showPasswordChange && (
                          <div className="h-9 px-3 flex items-center rounded-md border border-dashed border-border text-[11px] text-muted-foreground">
                            Password will stay unchanged unless you explicitly choose to update it.
                          </div>
                        )}
                        {errors.password && <p className="text-[10px] text-red-500 font-medium">{errors.password}</p>}
                      </div>

                      {/* 4. Role */}
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-muted-foreground">System Role</Label>
                        {isSuperAdmin ? (
                          <div className="h-9 px-3 flex items-center text-sm bg-purple-50 text-purple-700 border border-purple-100 rounded-md font-medium">
                            <Crown size={14} className="mr-2" /> Super Admin
                          </div>
                        ) : (
                          <select
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                          >
                            <option value="admin">Admin</option>
                            <option value="explorer">Explorer</option>
                          </select>
                        )}
                      </div>

                      {/* Designation Field */}
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-muted-foreground">Designation</Label>
                        <Input
                          value={formData.designation}
                          onChange={(e) => {
                            setFormData({ ...formData, designation: e.target.value });
                            setErrors((prev) => ({ ...prev, designation: null }));
                          }}
                          placeholder="e.g. CFO, Marketing Manager"
                          className={`h-9 ${errors.designation ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                        />
                        {errors.designation && (
                          <p className="text-[10px] text-red-500 font-medium">{errors.designation}</p>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-muted-foreground">Download Permissions</Label>
                        <select
                          value={isSuperAdmin ? "Yes" : formData.isDownloadAllowed}
                          disabled={isSuperAdmin}
                          onChange={(e) => setFormData({ ...formData, isDownloadAllowed: e.target.value })}
                          className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
                        >
                          <option value="Yes">Allowed</option>
                          <option value="No">Restricted</option>
                        </select>
                      </div>

                      {/* 6. TOKEN LIMIT (FULL WIDTH "CARD" DESIGN) */}
                      <div className="col-span-1 md:col-span-2 mt-2 pt-4 border-t border-border border-dashed">
                         <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            
                            <div className="flex flex-col">
                              <Label className="text-sm font-medium">Token Limit</Label>
                              <span className="text-[10px] text-muted-foreground">
                                {isDemoTenant
                                  ? `Set usage cap for AI generation. Demo users can use up to ${demoTokenLimit} tokens.`
                                  : "Set usage cap for AI generation"}
                              </span>
                            </div>

                            <div className="flex items-center gap-3 bg-muted/40 p-1.5 rounded-lg border border-border/50">
                              {isDemoTenant ? (
                                <label className={`cursor-pointer px-3 py-1.5 rounded-md text-xs font-medium transition-all ${tokenLimitMode === 'max_demo' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                                  <input
                                    type="radio"
                                    className="hidden"
                                    checked={tokenLimitMode === "max_demo"}
                                    onChange={() => {
                                      setTokenLimitMode("max_demo");
                                      setFormData(prev => ({ ...prev, tokenLimit: String(demoTokenLimit) }));
                                      setErrors(prev => ({ ...prev, tokenLimit: null }));
                                    }}
                                  />
                                  Max Demo Limit
                                </label>
                              ) : (
                                <label className={`cursor-pointer px-3 py-1.5 rounded-md text-xs font-medium transition-all ${tokenLimitMode === 'unlimited' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                                  <input
                                    type="radio"
                                    className="hidden"
                                    checked={tokenLimitMode === "unlimited"}
                                    onChange={() => {
                                      setTokenLimitMode("unlimited");
                                      setFormData(prev => ({ ...prev, tokenLimit: "" }));
                                      setErrors(prev => ({ ...prev, tokenLimit: null }));
                                    }}
                                  />
                                  Unlimited
                                </label>
                              )}

                              <label className={`cursor-pointer px-3 py-1.5 rounded-md text-xs font-medium transition-all ${tokenLimitMode === 'custom' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                                <input 
                                  type="radio" 
                                  className="hidden" 
                                  checked={tokenLimitMode === "custom"}
                                  onChange={() => {
                                    setTokenLimitMode("custom");
                                    setFormData(prev => ({
                                      ...prev,
                                      tokenLimit:
                                        isDemoTenant && Number(prev.tokenLimit || 0) > demoTokenLimit
                                          ? String(demoTokenLimit)
                                          : prev.tokenLimit,
                                    }));
                                  }}
                                />
                                Custom Limit
                              </label>
                              
                              {/* Input: Only shows when Custom is active */}
                              {tokenLimitMode === "custom" && (
                                // FIX 2: Changed w-24 to w-36 to fit millions (e.g., 1000000)
                                <div className="w-36 animate-in fade-in zoom-in-95 duration-200">
                                  <Input
                                    type="number"
                                    value={formData.tokenLimit}
                                    onChange={(e) => {
                                      const rawValue = e.target.value;
                                      const normalizedValue =
                                        isDemoTenant && rawValue !== ""
                                          ? String(Math.min(Number(rawValue || 0), demoTokenLimit))
                                          : rawValue;
                                      setFormData({ ...formData, tokenLimit: normalizedValue });
                                      setErrors((prev) => ({ ...prev, tokenLimit: null }));
                                    }}
                                    className={`h-7 text-xs ${errors.tokenLimit ? "border-red-500" : ""}`}
                                    placeholder={isDemoTenant ? `Max ${demoTokenLimit}` : "e.g. 1000000"}
                                    min="1"
                                    max={isDemoTenant ? demoTokenLimit : undefined}
                                    autoFocus
                                  />
                                </div>
                              )}
                            </div>
                         </div>
                         {errors.tokenLimit && <p className="text-[10px] text-red-500 font-medium mt-1 text-right">{errors.tokenLimit}</p>}
                      </div>

                    </div>
                  )}

                  {/* ====== STEP 2: DATABASE ACCESS ====== */}
                 {step === 2 && (
  <>
    {/* 🔥 Access Mode Tabs */}
    <div className="flex gap-1 p-1 bg-muted/50 rounded-xl border border-border mb-4">

  {[
    { id: "direct", label: "Direct Access", icon: Database },
    { id: "existing", label: "Existing Group", icon: Users },
    { id: "new", label: "Create Group", icon: UserPlus },
  ].map(({ id, label, icon: Icon }) => (
    <button
      key={id}
      type="button"
      onClick={() => handleAccessModeChange(id)}
      className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all
        ${accessMode === id
          ? "bg-background text-foreground shadow-sm border border-border"
          : "text-muted-foreground hover:text-foreground hover:bg-background/60"}`}
    >
      <Icon size={13} />
      {label}
    </button>
  ))}
</div>

                    
                    {/* ───── DIRECT ACCESS ───── */}
{accessMode === "direct" && (
  <div className="flex gap-3" style={{ minHeight: 220 }}>

    {/* Left: DB list */}
    <div className="w-36 shrink-0 space-y-1">
      <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Databases</p>
      <select
  className="w-full h-8 rounded-md border border-border bg-background px-2 text-xs mb-2"
  value=""
  onChange={(e) => {
    const id = Number(e.target.value);
    if (!Number.isInteger(id) || id <= 0) return;
    handleAddDB(id);
  }}
>
  <option value="">Select database…</option>

  {dbOptions
    .filter(db => !selectedDBs.some(d => d.id === db.id))
    .map(db => (
      <option key={db.id} value={db.id}>
        {db.display_label}
      </option>
    ))}
</select>

      {selectedDBs.length === 0 && (
        <p className="text-[10px] text-muted-foreground italic text-center pt-2">No DB added yet</p>
      )}

      {selectedDBs.map(db => {
        const active = activeDBId === db.id;
        return (
          <button key={db.id} type="button" onClick={() => setActiveDBId(db.id)}
            className={`w-full text-left px-2.5 py-2 rounded-xl text-xs transition-all border-2
              ${active
                ? "bg-primary text-primary-foreground border-primary shadow-md"
                : "bg-primary/10 text-foreground border-primary/30"}`}>
            <div className="flex items-center gap-1.5">
              <Database className="w-3 h-3 shrink-0 opacity-70" />
              <span className="font-semibold truncate">{db.title}</span>
            </div>
            <div className={`text-[9px] mt-0.5 font-bold ${active ? "opacity-80" : "text-primary"}`}>
              {db.assigned_tables.length} table{db.assigned_tables.length !== 1 ? "s" : ""}
            </div>
          </button>
        );
      })}
    </div>

    {/* Right: Table selector */}
    <div className="flex-1 border-l border-border pl-3 flex flex-col">
      {!activeDB ? (
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
          <Database className="w-8 h-8 opacity-10 mb-2" />
          <p className="text-[11px] text-center">Add a database and select it to configure tables</p>
        </div>
      ) : tableLoading ? (
        <div className="space-y-1.5">{[...Array(4)].map((_, i) => <SkeletonLoader key={i} className="h-7 rounded-lg" />)}</div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-border">
            <div>
              <p className="text-xs font-semibold text-foreground">{activeDB.title}</p>
              <p className="text-[10px] text-muted-foreground">
                {activeDB.assigned_tables.length}/{[...activeDB.assigned_tables, ...activeDB.available_tables].length} selected
              </p>
            </div>
            <div className="flex gap-2 text-[10px]">
              <button type="button"
                onClick={() => setSelectedDBs(p => p.map(db => db.id !== activeDBId ? db : {
                  ...db,
                  assigned_tables: [...db.assigned_tables, ...db.available_tables],
                  available_tables: []
                }))}
                className="text-primary hover:underline font-semibold">All</button>
              <span className="text-muted-foreground">·</span>
              <button type="button"
                onClick={() => setSelectedDBs(p => p.map(db => db.id !== activeDBId ? db : {
                  ...db,
                  available_tables: [...db.assigned_tables, ...db.available_tables],
                  assigned_tables: []
                }))}
                className="text-muted-foreground hover:underline">None</button>
              <span className="text-muted-foreground">·</span>
              <button type="button" onClick={() => handleRemoveDB(activeDB.id)}
                className="text-destructive hover:underline">Remove</button>
            </div>
          </div>

          <div className="flex-1 max-h-44 overflow-y-auto no-scrollbar space-y-0.5">
            {[...activeDB.assigned_tables, ...activeDB.available_tables].length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No tables found</p>
            ) : (
              [...activeDB.assigned_tables, ...activeDB.available_tables].map(t => {
                const checked = activeDB.assigned_tables.includes(t);
                return (
                  <label key={t} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-muted cursor-pointer group">
                    <input type="checkbox" checked={checked} onChange={() => toggleTable(t)}
                      className="rounded border-border text-primary focus:ring-primary h-3.5 w-3.5" />
                    <span className={`text-xs flex-1 transition-colors ${checked ? "text-foreground font-semibold" : "text-muted-foreground group-hover:text-foreground"}`}>{t}</span>
                    {checked && <CheckCircle2 className="w-3 h-3 text-primary shrink-0" />}
                  </label>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  </div>
)}

{/* ───── EXISTING GROUP ───── */}
{accessMode === "existing" && (
  <div className="space-y-3">
    <GroupCombobox
      groupOptions={groupOptions}
      selectedGroupId={selectedGroupId}
      onSelect={setSelectedGroupId}
    />
  </div>
)}

{/* ───── CREATE GROUP ───── */}
{accessMode === "new" && (
  <InlineCreateGroupForm
    onGroupCreated={handleNewGroupCreated}
    onCancel={() => setAccessMode("direct")}
  />
)}

  </>
 )}
  </div>

  {/* === FOOTER ACTIONS === */}
               
                <div className="p-4 border-t border-border bg-muted/10 flex justify-between items-center shrink-0 rounded-b-xl">
                  
                  {step === 1 ? (
                    <>
                      {/* Left Side: Cancel */}
                      <Button 
                        type="button" 
                        variant="ghost" 
                        onClick={handleCancel} 
                        className="h-9 text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </Button>
                      
                      {/* Right Side: Next */}
                      {/* Added SAFETY LOCK logic to prevent double-clicks */}
                      <Button 
                        type="button" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          if(validateStep1()) {
                            setIsTransitioning(true); 
                            setStep(2);               
                            setTimeout(() => setIsTransitioning(false), 500);
                          }
                        }} 
                        className="h-9 px-6"
                      >
                        Next Step <ChevronRight size={14} className="ml-1" />
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* Left Side: Back */}
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setStep(1)} 
                        className="h-9"
                      >
                        <ChevronLeft size={14} className="mr-1" /> Back
                      </Button>

                      {/* Right Side: Create/Update */}
                      {/* FIX 2: Added explicit disabled styling so the state is 'Clearly' visible */}
                      <Button 
                        type="submit" 
                        disabled={
                        isSubmitting ||
                        isTransitioning ||
                        (accessMode === "direct" && selectedDBs.length === 0) ||
                        (accessMode === "existing" && !selectedGroupId) ||
                        accessMode === "new"
                         }
                      className={`h-9 px-6 shadow-md transition-all duration-200 ${
                          (isSubmitting ||
                          isTransitioning ||
                          (accessMode === "direct" && selectedDBs.length === 0) ||
                          (accessMode === "existing" && !selectedGroupId) ||
                           accessMode === "new")
                           ? "opacity-50 cursor-not-allowed bg-muted-foreground/20 text-muted-foreground shadow-none"
                           : "hover:ring-2 hover:ring-primary/20 hover:scale-[1.02]"
                      }`}

                      >
                        {isSubmitting ? "Saving..." : editingUser ? "Update User" : "Create User"}
                      </Button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>,
          document.getElementById("modal-root")
        )}

      </div>
    );
}
